/**
 * Les rôles à nommer : plusieurs lignes d'équipe sur un même code de main-d'œuvre.
 *
 * *Demande de Chris, 02/09/2026 : « dans la contre-étude, dans Équipe du chantier, il faut
 * rajouter 3 lignes en plus des chefs de chantier, des chefs d'équipe et conduite de travaux :
 * MOI CONDUCTEUR ENGINS filtre = `_MOICDTEENG`, MOI ASSISTANTE ADMINISTRATIVE filtre =
 * `_MOIADMTRX`, MOI ASSISTANTE REFERENTE filtre = `_MOIADMTRX`. »*
 *
 * *Et, sur le pourquoi : « je veux bien identifier une assistante référente si on veut créer des
 * équipes de suivi administratif. »* La référente est donc une **responsabilité nommée**, pas une
 * administrative qui passe : c'est ce qui permettra de constituer ces équipes plus tard, et c'est
 * ce qui interdit de la confondre avec l'assistante sous prétexte qu'elles partagent un code.
 *
 * ## Ce que cet écran administre, et ce qu'il n'administre pas
 *
 * La case « à nommer » de la liste main-d'œuvre décide **quels codes portent un nom** — la règle
 * de la 018, inchangée. Cet écran-ci décide, pour un code coché, **en combien de rôles distincts
 * il se décline et sous quels noms**.
 *
 * Il ne redouble donc pas la case : il la précise, et son silence est un état défini. Un code
 * coché sans aucun rôle déclaré donne **une** ligne d'équipe, portant le libellé du référentiel —
 * ce que Chris a demandé pour les libellés, et ce que font les trois fonctions en place.
 *
 * ## Pourquoi le nom est obligatoire ici
 *
 * Deux rôles sur un même code afficheraient le même libellé de référentiel et seraient
 * indiscernables dans « Équipe du chantier ». Le nom du rôle est donc saisi, et c'est le seul
 * endroit du produit où un libellé d'affichage ne vient pas du référentiel — parce que c'est le
 * seul endroit où le référentiel ne peut pas répondre.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import type { Generique } from "@planif/moteur";
import {
  chargerFonctionsANommer,
  designerEtapeDesHeures,
  declarerFonction,
  renommerFonction,
  retirerFonction,
} from "./api";
import type { FonctionANommer } from "./api";

/** Un champ qui n'envoie sa valeur qu'une fois la saisie terminée. */
function Champ({
  valeur,
  enAttente,
  ariaLabel,
  onValider,
}: {
  valeur: string;
  enAttente: boolean;
  ariaLabel: string;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);

  return (
    <input
      className="saisie saisie--texte"
      value={brouillon ?? valeur}
      disabled={enAttente}
      aria-label={ariaLabel}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== valeur && saisi.trim() !== "") onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; fonctions: readonly FonctionANommer[] }
  | { phase: "erreur"; raison: string };

export function FonctionsANommer({
  generiques,
}: {
  /** Le référentiel MO : ses codes cochés « à nommer » sont les seuls qui peuvent porter un rôle. */
  generiques: readonly Generique[];
}): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [codeVoulu, setCodeVoulu] = useState("");
  const [nomVoulu, setNomVoulu] = useState("");

  const recharger = (): void => {
    chargerFonctionsANommer()
      .then(({ fonctions }) => {
        setEtat({ phase: "chargee", fonctions });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(recharger, []);

  const agir = (action: Promise<unknown>): void => {
    setEnAttente(true);
    setErreur(null);
    action
      .then(recharger)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(false);
      });
  };

  const coches = generiques.filter((g) => g.type === "MO" && g.aNommer);

  if (etat.phase === "chargement") return <p>Chargement des rôles à nommer…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const { fonctions } = etat;
  /** Les rôles réellement déclarés — ceux qui ont un identifiant. */
  const declares = fonctions.filter((f) => f.id !== null);
  const complet = codeVoulu !== "" && nomVoulu.trim() !== "";

  return (
    <section className="referentiel">
      <h2>
        Rôles à nommer — {fonctions.length} ligne(s) dans « Équipe du chantier »
        {declares.length > 0 && <> · {declares.length} déclaré(s)</>}
      </h2>

      <p className="etat">
        Ce sont les lignes que « Équipe du chantier » affiche, sur l'écran de contre-étude. Un code
        coché <strong>« à nommer »</strong> dans la liste main-d'œuvre ci-dessus en donne{" "}
        <strong>une</strong>, portant le libellé du référentiel. Déclarer des rôles ici le fait en
        donner <strong>plusieurs</strong>, chacun sous son nom — pour les cas où l'ERP paie sur un
        seul code des responsabilités que le chantier distingue.
      </p>
      <p className="note">
        Le code reste le <strong>filtre</strong> : il décide quels salariés se proposent en face de
        la ligne. Deux rôles sur le même code puisent donc dans le même vivier, et l'écran de
        l'équipe le dit. Un rôle sur lequel des personnes sont nommées ne peut plus être retiré : il
        porte leur histoire.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {coches.length === 0 && (
        <p className="alerte">
          Aucun code n'est coché « à nommer » dans la liste main-d'œuvre ci-dessus : il n'y a donc
          aucune ligne d'équipe, et aucun rôle à déclarer. Cocher d'abord les codes d'encadrement.
        </p>
      )}

      <div className="referentiel__defilement">
        <table className="ressources roles__table">
          <colgroup>
            <col style={{ width: "calc(16ch + 0.7rem)" }} />
            <col style={{ width: "calc(34ch + 0.7rem)" }} />
            <col style={{ width: "calc(14ch + 0.7rem)" }} />
            <col style={{ width: "calc(22ch + 0.7rem)" }} />
            <col style={{ width: "6rem" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Code (filtre)</th>
              <th>Ligne d'équipe</th>
              <th>Origine du nom</th>
              {/*
                **Qui valide les heures du chantier** (084, Chris 17/09/2026). Le circuit va du
                chantier au conducteur, puis à l'assistante référente, puis à la RH — celle-ci
                étant un titulaire de fonction, nommé dans l'écran Membres et non ici.

                Ce n'est pas la case « valide la CE » (057) : sur `_MOIADMTRX` cohabitent
                l'assistante administrative, qui valide les devis, et l'assistante référente, qui
                valide les heures. Les confondre aurait fait valider les heures par la mauvaise.
              */}
              <th title="Le circuit de validation des heures : le chantier déclare, puis la journée monte.">
                Valide les heures
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {fonctions.map((f) => (
              <tr key={f.id ?? f.codeMo}>
                <td className="code" title={f.partageLeCode ? "Plusieurs rôles partagent ce code : même vivier de candidats." : undefined}>
                  {f.codeMo}
                  {f.partageLeCode && <span className="note"> · partagé</span>}
                </td>
                <td>
                  {f.id === null ? (
                    // Le libellé vient du référentiel : le corriger ici donnerait une seconde
                    // source à une valeur qui en a déjà une. Il se corrige dans la liste MO.
                    <span className="designation">{f.libelle}</span>
                  ) : (
                    <Champ
                      valeur={f.libelle}
                      enAttente={enAttente}
                      ariaLabel={`Nom du rôle ${f.libelle}`}
                      onValider={(valeur) => {
                        agir(renommerFonction(f.id ?? "", valeur));
                      }}
                    />
                  )}
                </td>
                <td className="note">
                  {f.id === null ? "référentiel main-d'œuvre" : "saisi ici"}
                </td>
                <td>
                  {/*
                    **Toutes les lignes sont désignables, y compris celles sans identifiant.**

                    Une ligne à `id` nul est le rôle unique d'un code — le conducteur de travaux
                    en production. La première version ne proposait le choix que sur les rôles
                    déclarés, et rendait donc son étape impossible à désigner (085).
                  */}
                  <select
                    value={f.valideLesHeures ?? ""}
                    disabled={enAttente}
                    aria-label={`Étape du circuit des heures tenue par ${f.libelle}`}
                    onChange={(evenement) => {
                      const choix = evenement.target.value;
                      agir(
                        designerEtapeDesHeures(
                          { codeMo: f.codeMo, fonctionId: f.id },
                          choix === "" ? null : (choix as "conducteur" | "assistante"),
                        ),
                      );
                    }}
                  >
                    <option value="">—</option>
                    <option value="conducteur">1. Conducteur du chantier</option>
                    <option value="assistante">2. Assistante référente</option>
                  </select>
                </td>
                <td>
                  {f.id === null ? (
                    <span className="note" title="Cette ligne existe parce que le code est coché « à nommer ». Pour la retirer, décocher la case dans la liste main-d'œuvre.">
                      —
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="lien"
                      disabled={enAttente}
                      title="Retirer ce rôle. Refusé si des personnes y sont nommées sur un chantier."
                      onClick={() => {
                        agir(retirerFonction(f.id ?? ""));
                      }}
                    >
                      Retirer
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {fonctions.length === 0 && (
              <tr>
                <td colSpan={5} className="vide">
                  Aucune ligne d'équipe.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form
        className="ajout"
        onSubmit={(e) => {
          e.preventDefault();
          if (!complet) return;
          const code = codeVoulu;
          const nom = nomVoulu.trim();
          setNomVoulu("");
          agir(declarerFonction(code, nom));
        }}
      >
        <span className="periodes__titre">Nouveau rôle</span>
        {/*
          Le code se CHOISIT parmi les codes cochés, jamais ne se tape : un code inventé ici
          désignerait un vivier vide, et la ligne d'équipe ne proposerait personne. Le serveur le
          refuse de toute façon — cette liste évite d'avoir à le découvrir.
        */}
        <select
          value={codeVoulu}
          disabled={coches.length === 0 || enAttente}
          aria-label="Code main-d'œuvre du nouveau rôle"
          onChange={(e) => {
            setCodeVoulu(e.target.value);
          }}
        >
          <option value="">
            {coches.length === 0 ? "aucun code coché « à nommer »" : "Choisir un code…"}
          </option>
          {coches.map((g) => (
            <option key={g.id} value={g.code}>
              {g.code} — {g.libelle}
            </option>
          ))}
        </select>
        <input
          className="ajout__libelle"
          value={nomVoulu}
          placeholder="Nom du rôle, tel qu'il s'affichera"
          aria-label="Nom du nouveau rôle"
          onChange={(e) => {
            setNomVoulu(e.target.value);
          }}
        />
        <button type="submit" disabled={!complet || enAttente}>
          Déclarer
        </button>
      </form>

      {/*
        L'avertissement qui compte : déclarer le PREMIER rôle d'un code fait disparaître sa ligne
        unique. Les personnes déjà nommées dessus se retrouveraient sur une ligne qui n'existe
        plus — le serveur refuse alors, en disant combien elles sont. Dit avant le clic.
      */}
      <p className="note">
        Déclarer le <strong>premier</strong> rôle d'un code fait disparaître sa ligne unique : les
        personnes déjà nommées dessus n'auraient plus de ligne, et personne ne peut deviner lequel
        des nouveaux rôles chacune tient. C'est refusé tant qu'elles y sont — les retirer d'abord de
        leurs chantiers, puis les renommer sur le bon rôle.
      </p>
    </section>
  );
}
